/* eslint-disable react-hooks/exhaustive-deps */
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import SecurityIcon from "@mui/icons-material/Security";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import TablePreloader from "../../../components/Loader/TablePreloader";
import CancelModal from "../../../components/Modals/Cancel";
import SearchBar from "../../../components/SearchBar";
import Table from "../../../components/table/Table";
import {
  closeModal,
  deleteRole,
  getRoleData,
  getRoles,
  openModal,
  setPagination,
} from "../../../features/users/roleSlice";
import HomeLayout from "../../../layouts/Home";
import config from "../../../utils/config";
import Menu from "../../../utils/data.dropdown";
import { IError } from "../../../utils/icons.utils";

const { routes } = config;

const Roles = () => {
  const { roles, loading, pagination, modal } = useSelector(getRoleData);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [deleteData, setDeleteData] = useState({ name: "", id: "" });
  const [permissionsModal, setPermissionsModal] = useState({
    open: false,
    role: null,
  });

  const handleDeleteStaff = () => {
    dispatch(deleteRole(deleteData.id))
      .unwrap()
      .then((res) => {
        dispatch(getRoles());
      });
  };

  useEffect(() => {
    setIsLoading(false);
  }, []);

  useEffect(() => {
    (async () => await dispatch(getRoles()))();
  }, [pagination.page]);

  useEffect(() => {
    let cb = setTimeout(() => (async () => await dispatch(getRoles()))(), 700);

    return () => {
      clearTimeout(cb);
    };
  }, [pagination.search]);

  const handleViewPermissions = (role) => {
    setPermissionsModal({ open: true, role });
  };

  const handleClosePermissionsModal = () => {
    setPermissionsModal({ open: false, role: null });
  };

  // Group permissions by module for better display
  const groupPermissions = (permissions) => {
    const groups = {};
    permissions?.forEach((permission) => {
      const parts = permission.split("_");
      const module = parts.length > 1 ? parts.slice(1).join(" ") : "General";
      if (!groups[module]) {
        groups[module] = [];
      }
      groups[module].push(permission);
    });
    return groups;
  };

  const data = roles?.map((role) => ({
    Name: role?.name,
    "Permission Count": (
      <Chip
        label={`${role?.permissions?.length || 0} permissions`}
        size="small"
        sx={{
          backgroundColor: "#f3f4f6",
          color: "#4b5563",
          fontWeight: 500,
        }}
      />
    ),
    _data: role,
  }));

  const menu = new Menu({});
  const dropdownMenu = [
    menu.view({
      action: (role) => handleViewPermissions(role),
      noNavigate: true,
    }),
    menu.edit({
      navigate,
      route: routes.configurations.roleManagement.role,
    }),
    menu.delete({
      action: (role) => {
        setDeleteData((state) => ({ name: role?.name, id: role?.id }));
        dispatch(openModal("delete"));
      },
    }),
  ];

  return (
    <HomeLayout
      title="Role Management"
      subtitle="Configure roles and manage access permissions"
    >
      {/* Search and Action Bar */}
      <Box className="flex justify-between gap-4 mb-6 flex-col md:flex-row">
        <Box className="flex-1">
          <SearchBar
            {...{
              placeholder: "Search roles by name",
              onChange: (e) => {
                const { value } = e.target;
                if (e.code === "Enter") dispatch(getRoles(value));
                else dispatch(setPagination({ search: value }));
              },
            }}
          />
        </Box>
        <Button
          LinkComponent={Link}
          className="bg-[var(--c-primary-0)] hover:bg-[var(--c-primary-1)] shadow-md normal-case h-[56px] px-6 hidden rounded-lg"
          size="large"
          variant="contained"
          startIcon={<AddIcon />}
          to={routes.configurations.roleManagement.new}
        >
          Create Role
        </Button>
      </Box>

      {/* Table Card */}
      <Card className="shadow-sm">
        <CardContent sx={{ p: 0 }}>
          {isLoading ? (
            <TablePreloader />
          ) : data?.length ? (
            <Box className="p-4">
              <Table
                {...{
                  dropdownMenu,
                  data,
                  pagination,
                  setPagination: (d) => dispatch(setPagination(d())),
                  isLoading: loading,
                }}
              />
            </Box>
          ) : (
            <Box className="pt-12 pb-16 flex flex-col justify-center items-center">
              <img
                className="w-[280px] mb-6 opacity-75"
                src={IError}
                alt="No roles"
                loading="lazy"
              />
              <Typography className="text-xl text-[#040316] mb-2 font-medium">
                No roles found
              </Typography>
              <Typography className="text-sm text-gray-500 text-center max-w-md">
                {pagination.search
                  ? `No roles match your search "${pagination.search}"`
                  : "Get started by creating your first role"}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Permissions Modal */}
      <Dialog
        open={permissionsModal.open}
        onClose={handleClosePermissionsModal}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: "8px",
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle sx={{ p: 0, borderBottom: "1px solid #e5e7eb" }}>
          <Box
            sx={{
              paddingTop: "1rem",
              paddingBottom: "1rem",
              paddingLeft: "1.5rem",
              paddingRight: "1.5rem",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <Box
                sx={{ display: "flex", gap: "0.75rem", alignItems: "center" }}
              >
                <Box
                  sx={{
                    backgroundColor: "var(--c-primary-0)",
                    borderRadius: "8px",
                    width: "40px",
                    height: "40px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <SecurityIcon sx={{ color: "white", fontSize: 24 }} />
                </Box>
                <Box>
                  <Typography
                    variant="h6"
                    className="font-bold text-[#040316] capitalize"
                    sx={{
                      fontWeight: "bold",
                      "--tw-text-opacity": 1,
                      color: "rgb(4 3 22 / var(--tw-text-opacity))",
                    }}
                  >
                    {permissionsModal.role?.name}
                  </Typography>
                  <Typography
                    sx={{
                      "--tw-text-opacity": 1,
                      color: "rgb(107 114 128 / var(--tw-text-opacity))",
                    }}
                  >
                    {permissionsModal.role?.permissions?.length || 0} total
                    permissions
                  </Typography>
                </Box>
              </Box>
              <IconButton
                onClick={handleClosePermissionsModal}
                size="small"
                sx={{
                  color: "#6b7280",
                  "&:hover": {
                    backgroundColor: "#f3f4f6",
                  },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ p: 0, backgroundColor: "#f9fafb" }}>
          {permissionsModal.role?.permissions?.length > 0 ? (
            <Box sx={{ p: 3 }}>
              <Grid container spacing={2}>
                {Object.entries(
                  groupPermissions(permissionsModal.role?.permissions)
                ).map(([module, perms], index) => (
                  <Grid item xs={12} md={6} key={index}>
                    <Card
                      sx={{
                        height: "100%",
                        border: "1px solid #e5e7eb",
                        boxShadow: "0 1px 2px 0 rgba(0, 0, 0, 0.05)",
                        backgroundColor: "white",
                      }}
                    >
                      <CardContent sx={{ p: 2.5 }}>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            "--tw-border-opacity": 1,
                            borderColor:
                              "rgb(243 244 246 / var(--tw-border-opacity))",
                            borderBottomWidth: "1px",
                            marginBottom: "0.75rem",
                            paddingBottom: "0.5rem",
                          }}
                        >
                          <Typography
                            variant="subtitle2"
                            sx={{
                              fontSize: "0.875rem",
                              fontWeight: "bold",
                              "--tw-text-opacity": 1,
                              color: "rgb(4 3 22 / var(--tw-text-opacity))",
                            }}
                          >
                            {module}
                          </Typography>
                          <Chip
                            label={perms.length}
                            size="small"
                            sx={{
                              backgroundColor: "#f3f4f6",
                              color: "#6b7280",
                              fontWeight: 600,
                              fontSize: "0.7rem",
                              height: "20px",
                              minWidth: "24px",
                            }}
                          />
                        </Box>
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: 0.75,
                          }}
                        >
                          {perms.map((permission, idx) => (
                            <Box
                              key={idx}
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                padding: "6px 8px",
                                borderRadius: "4px",
                                backgroundColor: "#f9fafb",
                                transition: "all 0.15s",
                                "&:hover": {
                                  backgroundColor: "#f3f4f6",
                                },
                              }}
                            >
                              <Box
                                sx={{
                                  width: "4px",
                                  height: "4px",
                                  borderRadius: "50%",
                                  backgroundColor: "var(--c-primary-0)",
                                  flexShrink: 0,
                                }}
                              />
                              <Typography
                                sx={{
                                  fontSize: "0.8125rem",
                                  color: "#374151",
                                  lineHeight: 1.4,
                                  fontWeight: 500,
                                }}
                              >
                                {permission
                                  .split("_")
                                  .map(
                                    (word) =>
                                      word.charAt(0) +
                                      word.slice(1).toLowerCase()
                                  )
                                  .join(" ")}
                              </Typography>
                            </Box>
                          ))}
                        </Box>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          ) : (
            <Box
              sx={{
                textAlign: "center",
                paddingTop: "3rem",
                paddingBottom: "3rem",
              }}
            >
              <SecurityIcon sx={{ fontSize: 64, color: "#d1d5db", mb: 2 }} />
              <Typography
                sx={{
                  fontWeight: 600,
                  fontSize: "1.125rem",
                  lineHeight: "1.75rem",
                  "--tw-text-opacity": 1,
                  color: "rgb(55 65 81 / var(--tw-text-opacity))",
                }}
              >
                No permissions assigned
              </Typography>
              <Typography
                sx={{
                  fontSize: "0.875rem",
                  lineHeight: "1.25rem",
                  "--tw-text-opacity": 1,
                  color: "rgb(107 114 128 / var(--tw-text-opacity))",
                }}
              >
                This role doesn't have any permissions yet
              </Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <CancelModal
        {...{
          open: modal.delete,
          loading: modal.loading,
          title: "Delete Role",
          subtitle: `Are you sure you want to delete "${deleteData.name}"? This action cannot be undone.`,
          btnTexts: ["Cancel", "Delete"],
          handleSuccess: handleDeleteStaff,
          handleClose: () => dispatch(closeModal("delete")),
        }}
      />
    </HomeLayout>
  );
};

export default Roles;
